import { QueryKey } from '@tanstack/react-query';
import {
  IntegrationEnum,
  IntegrationStatus,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';
import { INTEGRATIONS_REQUIRING_SHARE_GUEST_INFO } from 'pages/integrations/IntegrationsTabs.constants';
import useShareGuestInfoModal from 'pages/integrations/modals/shareGuestInfoModal/useShareGuestInfoModal';
import useDisableHostfullyDevicesModal from 'pages/integrations/modals/disableHostfullyDevicesModal/useDisableHostfullyDevicesModal';
import useRedirectToIntegrationDetails from 'pages/integrations/hooks/useRedirectToIntegrationDetails';
import useActivateIntegration from './useActivateIntegration';

const useToggleSwitch = ({
  integrationSetting,
  queryKey,
}: {
  integrationSetting: InternalIntegrationSetting;
  queryKey: QueryKey;
}) => {
  const { disableIntegration } = useDisableIntegrationMutation();
  const { handleActivateIntegration } = useActivateIntegration();
  const { openShareGuestInfoModal } = useShareGuestInfoModal();
  const { openDisableHostfullyDevicesModal } =
    useDisableHostfullyDevicesModal();
  const { redirectToIntegrationDetails } = useRedirectToIntegrationDetails();

  const { enumId, integration, agencySettings } = integrationSetting;
  const { status } = agencySettings;
  const isActive = status === IntegrationStatus.ACTIVE;
  const isHostfullyDevices =
    integrationSetting.enumId === IntegrationEnum.HOSTFULLYDEVICES;

  const handleDisable = () => {
    if (isHostfullyDevices) {
      openDisableHostfullyDevicesModal({
        integrationEnumId: enumId,
        queryKey,
      });

      return;
    }

    disableIntegration({
      level: IntegrationMutationLevel.AGENCY,
      enumId,
      queryKey,
    });
  };

  const handleEnable = () => {
    const needsShareGuestInfo =
      INTEGRATIONS_REQUIRING_SHARE_GUEST_INFO.includes(
        integrationSetting.enumId,
      );

    const activateAndRedirect = () => {
      handleActivateIntegration({
        integration,
        queryKey,
        onSuccess: () => {
          redirectToIntegrationDetails(integration);
        },
      });
    };

    if (needsShareGuestInfo) {
      openShareGuestInfoModal({
        integration,
        onConfirm: () => {
          activateAndRedirect();
        },
      });

      return;
    }

    activateAndRedirect();
  };

  const handleToggle = () => {
    if (isActive) {
      handleDisable();
    } else {
      handleEnable();
    }
  };

  return { handleToggle };
};

export default useToggleSwitch;
